import { useEffect, useRef } from 'react';
import { layerInputId, resolveLayerResource, type CompositorNodeV3 } from '@/types/nodes/compositor';
import { useResolvedAssets } from '@/state/workflow.state';
import { assetUrl } from '@/utils/assetUrls';
import type { LayerId } from 'web';

interface AudioPlaybackParams {
  layers: CompositorNodeV3['data']['layers'] | undefined;
  /** What the compositor holds, needed to resolve what an audio layer plays. */
  resources: CompositorNodeV3['data']['resources'] | undefined;
  layerOrder: CompositorNodeV3['data']['layerOrder'] | undefined;
  compositorInputs: CompositorNodeV3['data']['input'] | undefined;
  currentTime: number;
  isPlaying: boolean;
  isBuffering?: boolean;
  globalMuted: boolean;
  /**
   * When false the hook does nothing — no elements are created or driven. Used to
   * hand playback to the Rust audio engine behind {@link FF_COMPOSITOR_AUDIO_ENGINE}
   * without touching the hook's internals, so it can be deleted wholesale later.
   */
  enabled?: boolean;
}

/**
 * Manages HTML Audio elements for audio layers in the compositor.
 * Syncs playback state (time, play/pause, mute) with the timeline.
 */
export function useAudioPlayback({
  layers,
  resources,
  layerOrder,
  compositorInputs,
  currentTime,
  isPlaying,
  isBuffering = false,
  globalMuted,
  enabled = true,
}: AudioPlaybackParams) {
  const audioElementsRef = useRef<Map<number, HTMLAudioElement>>(new Map());
  const resolvedAssets = useResolvedAssets();

  useEffect(() => {
    const audioElements = audioElementsRef.current;

    if (!enabled || !layerOrder || !layers || !resources) {
      return;
    }
    const lookup = { resources };

    // Collect audio layers and their URLs
    const activeAudioLayers = new Map<
      number,
      {
        key: string;
        fetchUrl: string;
        startTime: number;
        trimStart: number;
        duration: number;
        muted: boolean;
        volume: number;
        ancestorsInRange: boolean;
      }
    >();

    const withinRange = (startTime: number, duration: number) =>
      currentTime >= startTime && currentTime < startTime + duration;

    const collectAudioLayers = (
      ids: LayerId[],
      parentStartTime: number,
      ancestorsInRange: boolean,
      ancestorsMuted: boolean,
      ancestorsVolume: number,
    ) => {
      for (const layerId of ids) {
        const layer = layers[layerId];
        if (!layer) continue;

        const effectiveStartTime = parentStartTime + (layer.startTime ?? 0);
        const kind = layer.kind;
        const muted = ancestorsMuted || (kind.type === 'video' || kind.type === 'audio' ? kind.muted : false);
        // Animated volume isn't handled here; this legacy path is inactive in practice
        // because the Rust audio engine is enabled for all users.
        const volume =
          ancestorsVolume *
          ((kind.type === 'video' || kind.type === 'audio') && kind.volume.type === 'constant' ? kind.volume.value : 1);

        if (layer.kind.type === 'audio') {
          const audioKind = layer.kind;
          // An uploaded sound sits behind no wire; a wired one resolves its url
          // through the input. Play from whichever is present.
          const content = resolveLayerResource(lookup, layer)?.content;
          let file = content?.type === 'value' && content.data.url ? content.data : undefined;
          if (!file) {
            const inputId = layerInputId(lookup, layer);
            const matchedInput = compositorInputs?.find(([id, value]) => id === inputId && value != null);
            const inputFile = matchedInput?.[1]?.file;
            if (inputFile && inputFile.type === 'audio') {
              file = inputFile;
            }
          }
          const fetchUrl = assetUrl(file, resolvedAssets);
          if (file?.url && fetchUrl) {
            activeAudioLayers.set(layerId, {
              key: file.url,
              fetchUrl,
              startTime: effectiveStartTime,
              trimStart: audioKind.trimStart ?? 0,
              duration: layer.duration ?? 0,
              muted,
              volume,
              ancestorsInRange,
            });
          }
        }

        if (layer.children.length > 0) {
          const childrenInRange = ancestorsInRange && withinRange(effectiveStartTime, layer.duration ?? 0);
          collectAudioLayers(layer.children, effectiveStartTime, childrenInRange, muted, volume);
        }
      }
    };

    collectAudioLayers(layerOrder, 0, true, false, 1);

    // Remove audio elements for layers that no longer exist
    for (const [layerId, audio] of audioElements) {
      if (!activeAudioLayers.has(layerId)) {
        audio.pause();
        audio.src = '';
        audioElements.delete(layerId);
      }
    }

    // Create or update audio elements
    for (const [layerId, info] of activeAudioLayers) {
      let audio = audioElements.get(layerId);
      if (!audio) {
        audio = new Audio();
        audio.preload = 'auto';
        audioElements.set(layerId, audio);
      }

      // Compare on the stable key: a refreshed signed URL must not reload a playing clip.
      if (audio.dataset.key !== info.key) {
        audio.dataset.key = info.key;
        audio.src = info.fetchUrl;
      }

      const layerMuted = info.muted || globalMuted;
      audio.muted = layerMuted;
      // .volume and .muted are independent: a low volume still plays; mute silences regardless.
      audio.volume = Math.max(0, Math.min(1, info.volume));

      // Check if current time is within this layer's range and every ancestor
      // group's range.
      const layerStart = info.startTime;
      const layerEnd = info.startTime + info.duration;
      const isInRange = info.ancestorsInRange && currentTime >= layerStart && currentTime < layerEnd;

      if (isInRange && isPlaying && !isBuffering) {
        const audioTime = currentTime - layerStart + info.trimStart;
        // Only seek if significantly out of sync (avoid micro-seeks during playback)
        if (Math.abs(audio.currentTime - audioTime) > 0.3) {
          audio.currentTime = audioTime;
        }
        if (audio.paused) {
          void audio.play();
        }
      } else {
        if (!audio.paused) {
          audio.pause();
        }
        if (isInRange) {
          // Paused but in range — sync position for when play resumes
          const audioTime = currentTime - layerStart + info.trimStart;
          audio.currentTime = audioTime;
        }
      }
    }
  }, [
    enabled,
    layers,
    resources,
    layerOrder,
    compositorInputs,
    currentTime,
    isPlaying,
    isBuffering,
    globalMuted,
    resolvedAssets,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    const audioElements = audioElementsRef.current;
    return () => {
      for (const [, audio] of audioElements) {
        audio.pause();
        audio.src = '';
      }
      audioElements.clear();
    };
  }, []);
}
